import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-admin';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import { APIService } from '../../common/api.service';

// A prize reaches races on two paths:
//  - directly, through its own roomId (a prize created for one race)
//  - indirectly, through badges that hand out this prize; a badge covers every
//    race matched by its raceFilter
// PrizeDirectRace covers the first one, BadgeRaces the second.

// Link to the race the prize is attached to directly
export const PrizeDirectRace = ({ record }: any) => {
  if (!record) {
    return null;
  }

  if (!record.roomId) {
    return (
      <Typography variant="body2" color="textSecondary">
        Not attached to a race directly. It is only handed out through the badges below (if any).
      </Typography>
    );
  }

  return (
    <Button component={Link} to={`/room/${record.roomId}/show`} label={record.room?.event_title || `Race ${record.roomId}`}>
      <OpenInNewIcon />
    </Button>
  );
};

// Render the label the Tab passes in, the way the react-admin fields do
PrizeDirectRace.defaultProps = { addLabel: true };

interface BadgeRace {
  id: number;
  event_title: string;
  startDate?: string;
  endDate?: string;
  isPublic?: boolean;
  raceType?: string;
  distance?: number;
}

const formatDate = (date?: string) => (date ? new Date(date).toLocaleDateString() : '');

// Races matched by a badge's raceFilter. Resolved by the backend on demand
// (and cached there), so this only runs when an admin expands the badge row.
export const BadgeRaces = ({ record }: any) => {
  const [races, setRaces] = useState<BadgeRace[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!record || !record.id) {
      return;
    }
    let cancelled = false;
    APIService.calculateRaceIds(record.id)
      .then((data: any) => {
        if (!cancelled) {
          setRaces(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not load the races of this badge.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [record]);

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    );
  }

  if (!races) {
    return <Typography variant="body2">Loading races...</Typography>;
  }

  if (!races.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        This badge currently matches no race. Check its race filter.
      </Typography>
    );
  }

  return (
    <div>
      <Typography variant="body2" gutterBottom>
        {races.length} race(s) matched by this badge:
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Start</TableCell>
            <TableCell>End</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {races.map((race) => (
            <TableRow key={race.id}>
              <TableCell>{race.id}</TableCell>
              <TableCell>{race.event_title}</TableCell>
              <TableCell>{formatDate(race.startDate)}</TableCell>
              <TableCell>{formatDate(race.endDate)}</TableCell>
              <TableCell>
                <Button component={Link} to={`/room/${race.id}/show`} label="Show">
                  <OpenInNewIcon />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
